import localforage from 'localforage';

const STORE_NAME = 'gm-enrollment-report';
const SEARCH_PREFIX = 'search::';
const DATA_PREFIX = 'data::';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

localforage.config({
  name: STORE_NAME,
  storeName: 'kv',
  description: 'Offline cache for the GM Enrollment Report PWA',
});

const normalizeKey = (value) => String(value || '').trim().toLowerCase();

// Search cache functions
export async function saveOfflineSearch({ query, results, suggestions }) {
  const q = normalizeKey(query);
  if (!q) return;
  const payload = {
    query: String(query || '').trim(),
    cachedAt: new Date().toISOString(),
    results: Array.isArray(results) ? results : [],
    suggestions: Array.isArray(suggestions) ? suggestions : [],
  };
  await localforage.setItem(`${SEARCH_PREFIX}${q}`, payload);
}

export async function loadOfflineSearch(query) {
  const q = normalizeKey(query);
  if (!q) return null;
  const cached = await localforage.getItem(`${SEARCH_PREFIX}${q}`);
  if (!cached) return null;
  
  // Check if cache is expired
  const cachedAt = new Date(cached.cachedAt);
  const now = new Date();
  if (now - cachedAt > CACHE_EXPIRY_MS) {
    await localforage.removeItem(`${SEARCH_PREFIX}${q}`);
    return null;
  }
  
  return cached;
}

// Generic data cache functions
export async function saveOfflineData(key, data, metadata = {}) {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) return;
  
  const payload = {
    key: normalizedKey,
    data,
    metadata: {
      ...metadata,
      cachedAt: new Date().toISOString(),
    },
  };
  
  await localforage.setItem(`${DATA_PREFIX}${normalizedKey}`, payload);
}

export async function loadOfflineData(key) {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) return null;
  
  const cached = await localforage.getItem(`${DATA_PREFIX}${normalizedKey}`);
  if (!cached) return null;
  
  // Check if cache is expired
  const cachedAt = new Date(cached.metadata?.cachedAt);
  const expiry = cached.metadata?.expiryMs || CACHE_EXPIRY_MS;
  const now = new Date();
  
  if (now - cachedAt > expiry) {
    await localforage.removeItem(`${DATA_PREFIX}${normalizedKey}`);
    return null;
  }
  
  return cached.data;
}

export async function removeOfflineData(key) {
  const normalizedKey = normalizeKey(key);
  if (!normalizedKey) return;
  await localforage.removeItem(`${DATA_PREFIX}${normalizedKey}`);
}

export async function clearAllOfflineData() {
  const keys = await localforage.keys();
  const dataKeys = keys.filter((key) => key.startsWith(DATA_PREFIX) || key.startsWith(SEARCH_PREFIX));
  await Promise.all(dataKeys.map((key) => localforage.removeItem(key)));
}

export async function getOfflineDataSize() {
  const keys = await localforage.keys();
  const dataKeys = keys.filter((key) => key.startsWith(DATA_PREFIX) || key.startsWith(SEARCH_PREFIX));
  let totalSize = 0;
  
  for (const key of dataKeys) {
    const item = await localforage.getItem(key);
    if (item) {
      totalSize += JSON.stringify(item).length;
    }
  }
  
  return {
    count: dataKeys.length,
    sizeBytes: totalSize,
    sizeMB: (totalSize / (1024 * 1024)).toFixed(2),
  };
}

// Program enrollment data cache
export async function saveEnrollmentData(programName, data) {
  return saveOfflineData(`enrollment:${programName}`, data, {
    type: 'enrollment',
    programName,
  });
}

export async function loadEnrollmentData(programName) {
  return loadOfflineData(`enrollment:${programName}`);
}

// Dealer data cache
export async function saveDealerData(bac, data) {
  return saveOfflineData(`dealer:${bac}`, data, {
    type: 'dealer',
    bac,
  });
}

export async function loadDealerData(bac) {
  return loadOfflineData(`dealer:${bac}`);
}


